import React, {useCallback, useState} from 'react';
import {TermEntry, UseTermsManagerReturn} from './descriptor-types';
import {DEFAULT_TERM_ENTRIES, ERROR_MESSAGES, REQUIRED_TERMS} from './descriptor-constants';
import {OccurrenceReportDescriptorTerm} from '../../../../../@types/occurrence-report-descriptor';

/**
 * Custom hook for managing descriptor terms
 */
export function useTermsManager(initialTerms?: OccurrenceReportDescriptorTerm[]): UseTermsManagerReturn {
    // Initialize terms from descriptor or use defaults
    const [termEntries, setTermEntries] = useState<TermEntry[]>(() => {
        if (initialTerms && initialTerms.length > 0) {
            return initialTerms.map((term) => ({
                key: { text: term.term, error: false },
                value: { text: term.definition, error: false }
            }));
        }
        return DEFAULT_TERM_ENTRIES;
    });

    // Update term key
    const updateTermKey = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>, index: number) => {
        const key = event.target.value;
        setTermEntries(prev => {
            const newEntries = [...prev];
            
            if (key.trim() === '') {
                newEntries[index].key = { 
                    text: '', 
                    error: true, 
                    errorText: ERROR_MESSAGES.TERM_KEY_EMPTY 
                };
            } else if (prev.findIndex((entry, i) => i !== index && entry.key.text === key) >= 0) {
                newEntries[index].key = { 
                    text: key, 
                    error: true, 
                    errorText: ERROR_MESSAGES.TERM_KEY_EXISTS 
                };
            } else {
                newEntries[index].key = { text: key, error: false };
            }
            
            return newEntries;
        });
    }, []);

    // Update term value
    const updateTermValue = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>, index: number) => {
        const value = event.target.value;
        setTermEntries(prev => {
            const newEntries = [...prev];
            const keyText = newEntries[index].key.text;
            
            newEntries[index].value = { 
                text: value, 
                error: (REQUIRED_TERMS.includes(keyText as typeof REQUIRED_TERMS[number]) && value.trim() === '') 
            };
            
            return newEntries;
        });
    }, []);

    // Remove term
    const removeTerm = useCallback((index: number) => {
        setTermEntries(prev => prev.filter((_, i) => i !== index));
    }, []);

    // Add new term
    const handleAddNewTerm = useCallback(() => {
        setTermEntries(prev => {
            // Don't add if there's already an empty key
            if (prev.some(entry => entry.key.text === '')) {
                return prev;
            }
            return [...prev, { key: { text: '', error: false }, value: { text: '', error: false } }];
        });
    }, []);

    // Check if we can add new term
    const canAddNewTerm = useCallback((): boolean => {
        // Allow adding if there are no entries
        if (termEntries.length === 0) return true;
        
        // Don't allow if there's any empty key
        return !termEntries.some(entry => entry.key.text === '');
    }, [termEntries]);

    // Check if term can be removed
    const canRemoveTerm = useCallback((term: TermEntry): boolean => {
        return !REQUIRED_TERMS.includes(term.key.text as typeof REQUIRED_TERMS[number]);
    }, []);

    // Validate all terms
    const validateTerms = useCallback((): boolean => {
        return termEntries.every(entry => 
            !entry.key.error && 
            !entry.value.error && 
            entry.key.text && 
            entry.value.text
        );
    }, [termEntries]);

    // Convert to API format
    const getTermsForSubmission = useCallback((): OccurrenceReportDescriptorTerm[] => {
        return termEntries
            .filter(term => term.key.text && term.value.text)
            .map(entry => ({
                term: entry.key.text!,
                definition: entry.value.text!
            }));
    }, [termEntries]);

    // Load terms from descriptor
    const loadTerms = useCallback((terms: OccurrenceReportDescriptorTerm[]) => {
        if (terms && terms.length > 0) {
            setTermEntries(terms.map((term) => ({
                key: { text: term.term, error: false },
                value: { text: term.definition, error: false }
            })));
        } else {
            setTermEntries(DEFAULT_TERM_ENTRIES);
        }
    }, []);

    // Reset to default terms
    const resetTerms = useCallback(() => {
        setTermEntries(DEFAULT_TERM_ENTRIES);
    }, []);

    return {
        termEntries,
        updateTermKey,
        updateTermValue,
        removeTerm,
        handleAddNewTerm,
        canAddNewTerm,
        canRemoveTerm,
        validateTerms,
        getTermsForSubmission,
        loadTerms,
        resetTerms
    };
}